/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { ITimeUpdater } from "./ITimeUpdater";

export default class AnimationFrameTimeUpdater implements ITimeUpdater {
  private onAnimationFrame: VoidFunction;
  private animationFrameHandle: number;

  private lastFrameAt: number | null = null;
  private framesPerSecond: number;

  constructor(framesPerSecond?: number);
  constructor(onAnimationFrame: VoidFunction, framesPerSecond?: number);
  constructor(arg1?: VoidFunction | number, arg2?: number) {
    if (typeof arg1 === "function") {
      this.onAnimationFrame = arg1;
      this.framesPerSecond = arg2 ?? 18;
    } else {
      this.framesPerSecond = arg1 ?? 18;
    }
  }

  schedule(callback: VoidFunction): void {
    this.onAnimationFrame = callback;
  }

  resume(): void {
    this.suspend();
    this.onAnimationFrame();
    this.lastFrameAt = Date.now();
    this.requestUpdate();
  }

  suspend(): void {
    cancelAnimationFrame(this.animationFrameHandle);
  }

  private requestUpdate(): void {
    this.animationFrameHandle = requestAnimationFrame(() => {
      if (
        this.lastFrameAt &&
        Date.now() - this.lastFrameAt > 1000.0 / this.framesPerSecond
      ) {
        this.onAnimationFrame();
        this.lastFrameAt = Date.now();
      }

      this.requestUpdate();
    });
  }
}
